/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { isLocal } from '@squiz/dxp-console-support';
import { useAuthServiceStore, useConsoleServiceStore } from 'dxpConsoleCore-mf/State';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HttpHeaders, HttpResponse } from '../../../Services/http';
import { BaseService } from '../../_base';
import { ConsoleServiceConfiguration } from './Console.service.config';
import {
  ConsoleServiceCreateProductInstanceDto,
  ConsoleServiceGetBillerDetailsDto,
  ConsoleServiceGetBillerInvoicesDto,
  ConsoleServiceGetConnectWorkspacesDto,
  ConsoleServiceGetDxpCustomerSubscriptionsDto,
  ConsoleServiceGetDxpCustomersDto,
  ConsoleServiceGetDxpOrganisationDto,
  ConsoleServiceGetOrganisationMembersDto,
  ConsoleServiceGetProductInstancesDto,
  ConsoleServiceGetTenantLogsDto,
  ConsoleServiceGetTenantsDto,
  ConsoleServiceUpdateDxpOrganisationDto,
  ConsoleServiceUpdateOrganisationMemberDto,
  ConsoleServiceUpdateUserDto,
} from './Console.service.dto';
import {
  EntityApiInfo,
  EntityBillerDetails,
  EntityBillerInvoice,
  EntityConnectWorkspace,
  EntityConsoleHealthResponse,
  EntityConsoleInstance,
  EntityConsoleResponse,
  EntityDxpCustomers,
  EntityDxpOrganisation,
  EntityDxpSignupStatus,
  EntityDxpSubscription,
  EntityDxpTenant,
  EntityDxpUser,
  EntityDxpUserMe,
  EntityDxpUserMeOrganisation,
  EntityOrganisationMember,
  EntityOrganisationSubscriptions,
  EntityTenantLog,
} from './Entities';
import { EntityDxpServiceTokensResponse } from './Entities/DxpServiceTokensResponse/DxpServiceTokens.entity';
import { ConsoleServiceActivateDxpTenantDto } from './ServiceTypes/ActivateDxpTenant.dto';
import {
  ConsoleServiceActionDxpUserInviteDto,
  ConsoleServiceCancelDxpUserInviteDto,
  ConsoleServiceCheckSignupStatusDto,
  ConsoleServiceInviteDxpUsersDto,
  ConsoleServiceReInviteDxpUserDto,
  ConsoleServiceSignUpDxpUserDto,
} from './ServiceTypes/DxpUserOperations.dto';

export class ConsoleService extends BaseService {
  /**
   * Creates an instance of ConsoleService.
   */
  public constructor(configuration: ConsoleServiceConfiguration) {
    super(configuration);
  }

  /**
   * Handle response error case.
   */
  protected _handleResponseError<R>(value: HttpResponse<unknown>): Observable<R> {
    const statusCode = value.status as number;
    if (statusCode >= 400 && statusCode < 600) {
      // DXP Console backend catches server errors and returns in 'message' field.
      // If the field does not exist, it is from AWS itself.
      const responseBody = value.body as Record<string, string>;
      if (responseBody !== undefined && responseBody.message !== undefined) {
        throw new Error(responseBody.message);
      }
      throw new Error(value.statusText);
    }

    return of(value.body as R);
  }

  /**
   * Creates header object with Auth0 header to send api request.
   */
  protected getAuth0RequestHeaders(): HttpHeaders {
    const headers = this.getHeaders();

    // Use the stored state value for our auth service
    const authStore = useAuthServiceStore.getState();
    const authService = authStore.authService;
    const authUser = authStore.authUser;

    const authHeaders = authService.createAuthHeaders(authUser);
    if (authHeaders === null) {
      return headers;
    }

    for (const authHeader of authHeaders.entries()) {
      (headers as Headers).append(authHeader[0], authHeader[1]);
    }

    return headers;
  }

  /**
   * Action a user invite to DXP.
   */
  public actionUserInvite(dto: ConsoleServiceActionDxpUserInviteDto): Observable<EntityDxpUser> {
    const { accept, tenantId, userId } = dto;

    return this.httpService
      .patch<unknown, HttpResponse>(this.createUserUrl(`actionInvite`, userId), {
        body: {
          accept,
          tenantId,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpUser> => {
          return this._handleResponseError<EntityDxpUser>(value);
        }),
      );
  }

  /**
   * Append query param to the given url.
   */
  protected appendQueryParamsToUrl(queryParams: Array<string>, url: string): string {
    if (queryParams && queryParams.length > 0) {
      return `${url}?${queryParams.join('&')}`;
    }

    return url;
  }

  /**
   * Cancel a user invite to DXP.
   */
  public cancelUserInvite(dto: ConsoleServiceCancelDxpUserInviteDto): Observable<EntityConsoleResponse> {
    const { organisationId, userId } = dto;

    return this.httpService
      .delete<unknown, HttpResponse>(this.createUserUrl(`cancelInvite`, userId), {
        body: { organisationId },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Check the current status of a DXP signup flow.
   */
  public checkSignupStatus(dto: ConsoleServiceCheckSignupStatusDto): Observable<EntityDxpSignupStatus> {
    const { signupId, tenantId } = dto;

    const url = this.createSignupCheckUrl(signupId);
    const queryParams: Array<string> = [];
    queryParams.push(`tenantId=${tenantId}`);

    return this.httpService
      .get<unknown, HttpResponse>(this.appendQueryParamsToUrl(queryParams, url), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpSignupStatus> => {
          return this._handleResponseError<EntityDxpSignupStatus>(value);
        }),
      );
  }

  /**
   * Creates api url for _info endpoint.
   */
  protected createApiInfoUrl(): string {
    return `${this.sessionUrl}/_info`;
  }

  /**
   * Create and activate a new web product instance.
   */
  public activateProductInstance(dto: ConsoleServiceCreateProductInstanceDto): Observable<EntityConsoleInstance> {
    const { organisationId, ...rest } = dto;

    return this.httpService
      .put<unknown, HttpResponse>(this.createInstanceUrl(organisationId), {
        body: rest,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleInstance> => {
          return this._handleResponseError<EntityConsoleInstance>(value);
        }),
      );
  }

  /**
   * Creates api url for dxp product instance.
   */
  protected createInstanceUrl(organisationId?: string): string {
    return `${this.createOrganisationUrl(organisationId)}/instances`;
  }

  /**
   * Creates api url for dxp tenant.
   */
  protected createTenantUrl(): string {
    const url = `${this.sessionUrl}/tenants`;
    return url;
  }

  /**
   * Creates api url for dxp organisation.
   */
  protected createOrganisationUrl(organisationId?: string): string {
    const url = `${this.sessionUrl}/organisations`;
    return organisationId ? `${url}/${organisationId}` : url;
  }

  /**
   * Creates tenant logs url.
   */
  protected createTenantLogsUrl(organisationId: string): string {
    return `${this.createOrganisationUrl(organisationId)}/logs`;
  }

  /**
   * Creates tenant base url.
   */
  public createTenantBaseUrl(): string | null {
    const { tenant }: { tenant: EntityDxpUserMeOrganisation | null } = useConsoleServiceStore.getState();

    // Handle a rare case where the tenant could be null
    if (!tenant) {
      return null;
    }

    const { tenantReferenceId } = tenant;
    return `${this.sessionUrl}/tenants/${tenantReferenceId}`;
  }

  /**
   * Creates biller details url.
   */
  protected createBillerDetailsUrl(organisationId: string): string {
    return `${this.createOrganisationUrl(organisationId)}/biller`;
  }

  /**
   * Creates biller invoices url.
   */
  protected createBillerInvoicesUrl(organisationId: string): string {
    return `${this.createOrganisationUrl(organisationId)}/biller/invoices`;
  }

  /**
   * Creates api url for dxp organisation member.
   */
  protected createOrganisationMemberUrl(organisationId?: string, userId?: string): string {
    const url = `${this.createOrganisationUrl(organisationId)}/members`;
    return userId ? `${url}/${userId}` : url;
  }

  /**
   * Creates api url for dxp organisation's Connect contract.
   */
  protected createConnectContractUrl(organisationId?: string, connectContractId?: string): string {
    const url = `${this.createOrganisationUrl(organisationId)}/connectcontract`;
    return connectContractId ? `${url}/${connectContractId}` : url;
  }

  /**
   * Creates api url for dxp organisation's Connect workspaces.
   */
  protected createConnectWorkspacesUrl(organisationId: string, connectContractId: string): string {
    return `${this.createConnectContractUrl(organisationId, connectContractId)}/workspaces`;
  }

  /**
   * Creates api url for chargify customers.
   */
  protected createDxpCustomersUrl(): string {
    return `${this.sessionUrl}/chargify/customers`;
  }

  /**
   * Creates api url for chargify customer subscriptions.
   */
  protected createDxpCustomerSubscriptionsUrl(chargifySubDomain: string, customerId: number): string {
    return `${this.createDxpCustomersUrl()}/${customerId}/subscriptions?subdomain=${chargifySubDomain}`;
  }

  /**
   * Creates api url for users.
   */
  protected createUserUrl(type?: string, userId?: string): string {
    const url = `${this.sessionUrl}/users`;
    const user = userId || ``;

    switch (type) {
      case `me`:
        return `${url}/me`;
      case `newUser`:
        return `${url}/_dxpinvite`;
      case `existingUser`:
        return `${url}/${user}`;
      case `actionInvite`:
      case `cancelInvite`:
      case `reInvite`:
        return `${url}/${user}/_dxpinvite`;
      case `signUp`:
        return `${url}/_signup`;
      default:
        return url;
    }
  }

  /**
   * Creates api url for checking the status of a DXP signup flow.
   */
  protected createSignupCheckUrl(signupId: string): string {
    return `${this.sessionUrl}/users/_signup/${signupId}/status`;
  }

  /**
   * Get API info.
   */
  public getApiInfo(): Observable<EntityApiInfo> {
    return this.httpService
      .get<unknown, HttpResponse>(this.createApiInfoUrl(), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityApiInfo> => {
          return this._handleResponseError<EntityApiInfo>(value);
        }),
      );
  }

  /**
   * Get a DXP Organisation.
   */
  public getDxpOrganisation(dto: ConsoleServiceGetDxpOrganisationDto): Observable<EntityDxpOrganisation> {
    const { organisationId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(this.createOrganisationUrl(organisationId), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpOrganisation> => {
          return this._handleResponseError<EntityDxpOrganisation>(value);
        }),
      );
  }

  /**
   * Get all tenants matching query parameters.
   */
  public getTenants(dto: ConsoleServiceGetTenantsDto): Observable<Array<EntityDxpOrganisation>> {
    const { parentOrganisationId, permissionFilter, tenantType } = dto;
    const queryParams: Array<string> = [];
    if (parentOrganisationId) {
      queryParams.push(`parentOrganisationId=${parentOrganisationId}`);
    }

    if (permissionFilter) {
      queryParams.push(`permissionFilter=${permissionFilter}`);
    }

    if (tenantType) {
      queryParams.push(`tenantType=${tenantType}`);
    }
    return this.httpService
      .get<unknown, HttpResponse>(this.appendQueryParamsToUrl(queryParams, this.createTenantUrl()), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<Array<EntityDxpOrganisation>> => {
          return this._handleResponseError<Array<EntityDxpOrganisation>>(value);
        }),
      );
  }

  /**
   * Attempt to get all organisation members.
   */
  public getOrganisationMembers(
    dto: ConsoleServiceGetOrganisationMembersDto,
  ): Observable<Array<EntityOrganisationMember>> {
    const { organisationId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(this.createOrganisationMemberUrl(organisationId), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<Array<EntityOrganisationMember>> => {
          return this._handleResponseError<Array<EntityOrganisationMember>>(value);
        }),
      );
  }

  /**
   * Attempt to get all tenant logs.
   */
  public getTenantLogs(dto: ConsoleServiceGetTenantLogsDto): Observable<Array<EntityTenantLog>> {
    const { organisationId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(this.createTenantLogsUrl(organisationId), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<Array<EntityTenantLog>> => {
          return this._handleResponseError<Array<EntityTenantLog>>(value);
        }),
      );
  }

  /**
   * Attempt to get tenant subscription.
   */
  public getTenantSubscription(): Observable<EntityOrganisationSubscriptions | EntityDxpSubscription | null> {
    const baseUrl = this.createTenantBaseUrl();
    if (!baseUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = `${baseUrl}/subscription`;
    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap(
          (value: HttpResponse<unknown>): Observable<EntityOrganisationSubscriptions | EntityDxpSubscription> => {
            return this._handleResponseError<EntityOrganisationSubscriptions | EntityDxpSubscription>(value);
          },
        ),
      );
  }

  /**
   * Attempt to get biller details.
   */
  public getBillerDetails(dto: ConsoleServiceGetBillerDetailsDto): Observable<EntityBillerDetails> {
    const { organisationId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(this.createBillerDetailsUrl(organisationId), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<EntityBillerDetails> => {
          return this._handleResponseError<EntityBillerDetails>(value);
        }),
      );
  }

  /**
   * Attempt to get biller invoices.
   */
  public getBillerInvoices(dto: ConsoleServiceGetBillerInvoicesDto): Observable<Array<EntityBillerInvoice>> {
    const { organisationId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(this.createBillerInvoicesUrl(organisationId), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<Array<EntityBillerInvoice>> => {
          return this._handleResponseError<Array<EntityBillerInvoice>>(value);
        }),
      );
  }

  /**
   * Attempt to get Connect workspaces.
   */
  public getConnectWorkspaces(dto: ConsoleServiceGetConnectWorkspacesDto): Observable<Array<EntityConnectWorkspace>> {
    const { connectContractId, organisationId, workspaceType } = dto;
    const queryParams: Array<string> = [];

    if (workspaceType) {
      queryParams.push(`type=${workspaceType}`);
    }

    return this.httpService
      .get<unknown, HttpResponse>(
        this.appendQueryParamsToUrl(queryParams, this.createConnectWorkspacesUrl(organisationId, connectContractId)),
        {
          ...this.getSessionRequestOptions(),
        },
      )
      .pipe(
        switchMap((value: HttpResponse): Observable<Array<EntityConnectWorkspace>> => {
          return this._handleResponseError<Array<EntityConnectWorkspace>>(value);
        }),
      );
  }

  /**
   * Gets all console instances.
   */
  public getProductInstances(dto: ConsoleServiceGetProductInstancesDto): Observable<Array<EntityConsoleInstance>> {
    const { purpose, organisationId, typeFilter, statusFilter } = dto;
    const queryParams: Array<string> = [];
    if (purpose) {
      queryParams.push(`purpose=${purpose}`);
    }

    if (typeFilter) {
      queryParams.push(`type=${typeFilter}`);
    }

    if (statusFilter) {
      queryParams.push(`status=${statusFilter}`);
    }

    return this.httpService
      .get<unknown, HttpResponse>(this.appendQueryParamsToUrl(queryParams, this.createInstanceUrl(organisationId)), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Array<EntityConsoleInstance>> => {
          return this._handleResponseError<Array<EntityConsoleInstance>>(value);
        }),
      );
  }

  /**
   * Attempt to get dxp customers.
   */
  public getDxpCustomers(dto: ConsoleServiceGetDxpCustomersDto): Observable<EntityDxpCustomers> {
    const { customerType } = dto;
    const queryParams: Array<string> = [];
    if (customerType) {
      queryParams.push(`customerType=${customerType}`);
    }

    return this.httpService
      .get<unknown, HttpResponse>(this.appendQueryParamsToUrl(queryParams, this.createDxpCustomersUrl()), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<EntityDxpCustomers> => {
          return this._handleResponseError<EntityDxpCustomers>(value);
        }),
      );
  }

  /**
   * Attempt to get dxp customer subscriptions.
   */
  public getDxpCustomerSubscriptions(
    dto: ConsoleServiceGetDxpCustomerSubscriptionsDto,
  ): Observable<EntityOrganisationSubscriptions> {
    const { chargifySubDomain, customerId } = dto;
    return this.httpService
      .get<unknown, HttpResponse>(
        this.appendQueryParamsToUrl([], this.createDxpCustomerSubscriptionsUrl(chargifySubDomain, customerId)),
        {
          ...this.getSessionRequestOptions(),
        },
      )
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<EntityOrganisationSubscriptions> => {
          return this._handleResponseError<EntityOrganisationSubscriptions>(value);
        }),
      );
  }

  /**
   * Attempt to activate a tenant.
   */
  public activateTenant(dto: ConsoleServiceActivateDxpTenantDto): Observable<EntityDxpTenant> {
    return this.httpService
      .put<unknown, HttpResponse>(this.appendQueryParamsToUrl([], this.createTenantUrl()), {
        body: dto,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse<unknown>): Observable<EntityDxpTenant> => {
          return this._handleResponseError<EntityDxpTenant>(value);
        }),
      );
  }

  /**
   * Gets logged-in user details.
   */
  public getUserMe(): Observable<EntityDxpUserMe> {
    return this.httpService
      .get<unknown, HttpResponse>(this.createUserUrl(`me`), {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpUserMe> => {
          return this._handleResponseError<EntityDxpUserMe>(value);
        }),
        catchError((value) => {
          return of(value);
        }),
      );
  }

  /**
   * Invite new users to DXP.
   */
  public inviteUsers(dto: ConsoleServiceInviteDxpUsersDto): Observable<EntityConsoleResponse> {
    return this.httpService
      .post<unknown, HttpResponse>(this.createUserUrl(`newUser`), {
        body: dto,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Log in to the DXP Console with Auth0 token.
   */
  public logInWithAuth0(): Observable<EntityConsoleResponse> {
    const url = `${this.sessionUrl}/access/login`;

    // Workaround to enable secure cookies in localhost
    const withCredentials = isLocal();

    return this.httpService
      .post<unknown, HttpResponse>(url, {
        body: {},
        headers: this.getAuth0RequestHeaders(),
        withCredentials,
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Log in to the DXP Console with an IdP session.
   */
  public logInWithIdpSession(sessionToken: string): Observable<EntityConsoleResponse> {
    const url = `${this.sessionUrl}/access/login`;

    return this.httpService
      .post<unknown, HttpResponse>(url, {
        body: {
          token: sessionToken,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Retrieves the list of DXP service tokens associated with the logged in user.
   */
  public getDxpServiceTokens(): Observable<EntityDxpServiceTokensResponse> {
    const url = `${this.sessionUrl}/access/service`;

    return this.httpService
      .get<unknown, HttpResponse>(url, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpServiceTokensResponse> => {
          return this._handleResponseError<EntityDxpServiceTokensResponse>(value);
        }),
      );
  }

  /**
   * Re-invite an existing user to DXP.
   */
  public reInviteUser(dto: ConsoleServiceReInviteDxpUserDto): Observable<EntityConsoleResponse> {
    const { organisationId, userId } = dto;

    return this.httpService
      .post<unknown, HttpResponse>(this.createUserUrl(`reInvite`, userId), {
        body: { organisationId },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Complete sign up for an existing DXP user.
   */
  public signUp(dto: ConsoleServiceSignUpDxpUserDto): Observable<EntityConsoleResponse> {
    return this.httpService
      .post<unknown, HttpResponse>(this.createUserUrl(`signUp`), {
        body: dto,
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleResponse> => {
          return this._handleResponseError<EntityConsoleResponse>(value);
        }),
      );
  }

  /**
   * Attempt to update dxp organisation.
   */
  public updateDxpOrganisation(dto: ConsoleServiceUpdateDxpOrganisationDto): Observable<EntityDxpOrganisation> {
    const {
      activeDatastore,
      chargifySubDomain,
      clientId,
      connectContractId,
      maximumDatastoreInstances,
      name,
      organisationId,
      partnerClientId,
    } = dto;

    return this.httpService
      .patch<unknown, HttpResponse>(this.createOrganisationUrl(organisationId), {
        body: {
          activeDatastore,
          chargifySubDomain,
          clientId,
          connectContractId,
          maximumDatastoreInstances,
          name,
          partnerClientId,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpOrganisation> => {
          return this._handleResponseError<EntityDxpOrganisation>(value);
        }),
      );
  }

  /**
   * Uploads an organisation member.
   */
  public updateOrganisationMember(
    dto: ConsoleServiceUpdateOrganisationMemberDto,
  ): Observable<EntityOrganisationMember> {
    const { organisationId, roles, userId } = dto;
    return this.httpService
      .put<unknown, HttpResponse>(this.createOrganisationMemberUrl(organisationId, userId), {
        body: { roles },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityOrganisationMember> => {
          return this._handleResponseError<EntityOrganisationMember>(value);
        }),
      );
  }

  /**
   * Attempt to update user details.
   */
  public updateUser(dto: ConsoleServiceUpdateUserDto): Observable<EntityDxpUser> {
    const { familyName, givenName, userId } = dto;
    return this.httpService
      .patch<unknown, HttpResponse>(this.createUserUrl(`existingUser`, userId), {
        body: { familyName, givenName },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityDxpUser> => {
          return this._handleResponseError<EntityDxpUser>(value);
        }),
      );
  }

  /**
   * Get the console health.
   */
  public getHealth(): Observable<EntityConsoleHealthResponse> {
    const url = `${this.sessionUrl}/health`;

    return this.httpService
      .get<unknown, HttpResponse>(url, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityConsoleHealthResponse> => {
          return this._handleResponseError<EntityConsoleHealthResponse>(value);
        }),
      );
  }
}
